/* eslint-disable jsx-a11y/media-has-caption */
import { useCallback, useEffect } from 'react'

import { Drawer, Loader } from '@mantine/core'

import { navigate, routes } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import SwitchCompanyModal from 'src/components/Overlays/SwitchCompanyModal/SwitchCompanyModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useCallDrawerContext } from 'src/context/CallDrawerContext'
import { GET_ACTIVITY_LOG } from 'src/graphql/activityLogs.graphql'
import { useCallCompanySwitch } from 'src/hooks/useCallCompanySwitch'

import CallDetails from './CallDetails'

const CallDrawer = () => {
  const {
    activityLogId,
    opened: callDrawerOpened,
    close,
  } = useCallDrawerContext()

  const { data, loading } = useQuery(GET_ACTIVITY_LOG, {
    variables: {
      id: activityLogId,
    },
    skip: !activityLogId,
  })

  const activityLog = data?.activityLog
  const notFound =
    callDrawerOpened && !!activityLogId && !loading && !activityLog

  // The API returns null for a call the user can't access (e.g. a link to
  // another company's call). Offer to switch to the owning company when the
  // user can act there; otherwise send them to their dashboard instead of
  // rendering a drawer with nothing in it.
  const leaveDrawer = useCallback(() => {
    close()
    toast('Call not found', 'error')
    navigate(routes.dashboard())
  }, [close])

  const {
    handleCallNotFound,
    switchTarget,
    isResolving,
    isSwitching,
    confirmSwitch,
    cancelSwitch,
  } = useCallCompanySwitch(activityLogId, callDrawerOpened, leaveDrawer)

  useEffect(() => {
    if (notFound) {
      handleCallNotFound()
    }
  }, [notFound])

  return (
    <>
      <SwitchCompanyModal
        opened={!!switchTarget}
        applicantName={
          switchTarget?.applicantName
            ? `The call with ${switchTarget.applicantName}`
            : 'This call'
        }
        companyName={switchTarget?.companyName ?? ''}
        isSwitching={isSwitching}
        onCancel={cancelSwitch}
        onConfirm={confirmSwitch}
      />
      {/* Hidden (not closed) behind the prompt so it can't stack over it;
          the drawer context stays open for when the call loads. */}
      <Drawer
        opened={callDrawerOpened && !switchTarget}
        withCloseButton={false}
        onClose={close}
        position="right"
        size={'30%'}
        offset={6}
        radius="md"
        stackId="call-drawer"
        overlayProps={{ backgroundOpacity: 0.2 }}
        lockScroll={false}
        classNames={{
          title: 'text-base font-semibold text-doubleNickel-gray-900 ',
        }}
        styles={{
          content: {
            height: '100vh',
            maxHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
          body: {
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        {loading || isResolving ? (
          <Loader size={'sm'} color="gray" />
        ) : activityLog ? (
          <CallDetails activityLog={activityLog} close={close} />
        ) : null}
      </Drawer>
    </>
  )
}

export default CallDrawer
